import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, FileText, MapPin } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency } from "@/lib/format";
import { useAdminOrgStore } from "@/stores/admin-org";

/** Small pieces the Menus screens share (POS2-1456). */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** The Menus pages are site-scoped: with "All sites" in the picker they ask for one. */
export function PickASite() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const setSite = useAdminOrgStore((s) => s.setSite);
  const sites = useQuery<{ id: string; name: string }[]>({
    queryKey: ["menus-pick-site"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) throw new Error("Failed to load sites");
      return (await res.json()).data;
    },
  });

  return (
    <div className="mx-auto flex max-w-5xl flex-col items-center rounded-lg border border-black/[0.06] bg-white px-6 py-14 text-center">
      <MapPin className="size-8 text-gray-300" />
      <p className="mt-3 text-sm font-medium text-gray-900">{t("menus.pickSite.title")}</p>
      <p className="mt-1 max-w-sm text-sm text-gray-500">{t("menus.pickSite.description")}</p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {sites.isPending
          ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8 w-28" />)
          : (sites.data ?? []).map((site) => (
              <Button
                key={site.id}
                variant="outline"
                size="sm"
                onClick={() => setSite(site.id, site.name)}
              >
                {site.name}
              </Button>
            ))}
      </div>
    </div>
  );
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between rounded-lg border border-black/[0.06] bg-white px-4 py-3">
      <p className="text-sm text-red-600">{t("menus.loadError")}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("menus.retry")}
      </Button>
    </div>
  );
}

/** The title of the Menus page that opened this one, set by `useOpen`; none for a page reached
 * any other way (a link from elsewhere, the address bar). */
function useOpenedFrom(): string | null {
  return (useLocation().state as { back?: string } | null)?.back ?? null;
}

/** Opens another Menus page whose back link comes back here, to the page titled `title`. */
export function useOpen(): (to: string, title: string) => void {
  const navigate = useNavigate();
  return (to, title) => navigate(to, { state: { back: title } });
}

/** Back to the page that opened this one, as the browser's back does, so the history stays in
 * order; else up to `to`. */
export function useGoBack(to: string): () => void {
  const navigate = useNavigate();
  const openedFrom = useOpenedFrom();
  return () => (openedFrom ? navigate(-1) : navigate(to));
}

/** "< the page this one was opened from", else "< label" up to `to`. */
export function BackLink({ label, to }: { label: string; to: string }) {
  const openedFrom = useOpenedFrom();
  const goBack = useGoBack(to);
  return (
    <button
      type="button"
      onClick={goBack}
      className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
    >
      <ChevronLeft className="size-4" />
      {openedFrom ?? label}
    </button>
  );
}

/** A recipe count, or a dash while recipes are being read. */
export function RecipeCount({ count }: { count: number | null }) {
  if (count === null) return <span className="text-sm text-gray-300">-</span>;
  return (
    <span className="flex items-center gap-1.5 text-sm tabular-nums text-gray-900">
      <FileText className="size-3.5 text-gray-400" />
      {count}
    </span>
  );
}

/** Cost per portion, with cost per kg beneath it when the portion weight is known. */
export function CostCell({
  perPortion,
  weightKg,
  currency,
}: {
  perPortion: number | null;
  weightKg: number | null;
  currency: string;
}) {
  const { t, locale } = useTranslation();
  if (perPortion === null) return <span className="text-sm text-gray-300">-</span>;
  const money = (amount: number) => formatCurrency(locale, amount, currency, 2);
  return (
    <>
      <p className="text-sm font-medium tabular-nums text-gray-900">{money(perPortion)}</p>
      {weightKg ? (
        <p className="text-xs tabular-nums text-gray-400">
          {t("menus.perKg", { value: money(perPortion / weightKg) })}
        </p>
      ) : null}
    </>
  );
}

/** Skeleton rows standing in for a list while it loads. */
export function RowsSkeleton() {
  return (
    <div className="divide-y divide-black/[0.06] overflow-hidden rounded-lg border border-black/[0.06] bg-white">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <Skeleton className="h-5 w-8" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-72" />
          </div>
          <Skeleton className="hidden h-4 w-40 sm:block" />
        </div>
      ))}
    </div>
  );
}
